import React from "react";
import { useShallow } from "zustand/react/shallow";
import {
    GameConstant,
    RoomPlayer,
    RoomPlayerState,
    RoomState
} from "@uno/protocol";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomScene from "store/useStoreRoomScene";
import CircleProgress from "./CircleProgress";
import RoomPlayerAction from "../RoomPlayerAction";
import RoomPlayerEmote from "../RoomPlayerEmote";
import SwapTarget from "./Action/SwapTarget";
import Card from "component/Card/Card";
import Avatar from "component/Ui/Avatar";
import Tooltip from "component/Tooltip";
import useStoreRoomChat from "store/useStoreRoomChat";
import {
    CARD_WIDTH,
    CARD_HEIGHT
} from "types/Card";
import { useRoomLayout } from "context/RoomLayoutContext";

type OpponentProps = {
    roomPlayer: RoomPlayer | null,
    seat?: RoomSeat,
}

export type RoomSeat =
    | "top"
    | "top-left"
    | "left"
    | "bottom-left"
    | "bottom"
    | "bottom-right"
    | "right"
    | "top-right";

const Opponent = React.memo(function Opponent({ roomPlayer, seat }: OpponentProps) {
    const room = useStoreRoom(useShallow(state => ({ id: state.room?.id, state: state.room?.state })));
    const cards = useStoreRoomScene(useShallow(state => state.cards[roomPlayer?.player.id || ""] || []));
    const { scales, opponentHandMarginTop } = useRoomLayout();
    const opponentScale = scales.opponent;
    const [showUsername, setShowUsername] = React.useState(!!roomPlayer);
    const [usernameKey, setUsernameKey] = React.useState(0);
    const isTyping = useStoreRoomChat((state) => !!state.typingPlayers[roomPlayer?.player.id || ""]);

    const roomIsPlaying = React.useMemo(() => room?.state === RoomState.Playing, [room?.state]);
    const isActive = React.useMemo(() => roomIsPlaying && [RoomPlayerState.Active, RoomPlayerState.AwaitingAction].includes(roomPlayer?.state || RoomPlayerState.Inactive), [roomIsPlaying, roomPlayer?.state]);
    const cardsCount = React.useMemo(() => roomPlayer?.cards.length || 0, [roomPlayer?.cards.length]);

    const handCount = React.useMemo(() => cards.filter(card => card.type === "BACK").length, [cards]);
    const playedCardOffsets = React.useRef<Record<string, number>>({});
    const visibleCount = Math.min(handCount, GameConstant.startingCardsCount);
    const cardGap = React.useMemo(() => {
        const baseGap = CARD_WIDTH * 0.08;
        const growthPerCard = CARD_WIDTH * 0.005;

        if(visibleCount <= 1) {
            return 0;
        }

        return baseGap + (visibleCount - 2) * growthPerCard;
    }, [visibleCount]);

    const renderCards = React.useMemo(() => {
        const offsets: Record<string, number> = {};
        let index = -1;

        const rendered = cards.map((card) => {
            const isPlayed = card.type !== "BACK";

            if(!isPlayed) {
                index++;
            }

            const offset = isPlayed
                ? playedCardOffsets.current[card.id] ?? 0
                : ((index <= GameConstant.startingCardsCount ? index : GameConstant.startingCardsCount - 1) - (visibleCount - 1) / 2) * cardGap;

            offsets[card.id] = offset;

            return (
                <Card key={card.id} id={card.id} type={card.type} isHoverable={false} style={{
                    visibility: !isPlayed && index > GameConstant.startingCardsCount ? "hidden" : "visible",
                    position: "absolute",
                    transform: `translateX(${offset / 2}px) scale(${opponentScale})`,
                    transition: "transform 160ms ease-out",
                    zIndex: isPlayed ? cards.length * 3 : cards.length + index,
                }} />
            );
        });

        playedCardOffsets.current = offsets;

        return rendered;
    }, [
        cardGap,
        cards,
        cardsCount,
        opponentScale,
        visibleCount,
    ]);

    const isAbandoned = roomPlayer?.state === RoomPlayerState.Abandoned;

    return (
        <div className={`opponent z-[25] ${!roomPlayer ? "" : ""} ${isActive ? "active-halo" : ""} ${isAbandoned ? "opacity-30" : ""}`} data-player-id={roomPlayer?.player.id} data-seat={seat} style={
            roomPlayer ? (isActive
                ? { border: "none" }
                : { borderStyle: "solid" }
            ) : { background: "rgba(255, 255, 255, 0.15)", borderStyle: "dotted" }
        }>
            {roomPlayer && (
                <>
                    <div className="cards-wrapper flex" style={{ marginTop: `${opponentHandMarginTop}px` }}>
                        <div className="cards-container flex items-center justify-center">
                            {renderCards}

                            {cardsCount > GameConstant.startingCardsCount && (
                                <div className="absolute rounded-sm" style={{
                                    background: "linear-gradient(160deg, rgba(255,255,255,1) 30%, rgba(172,153,111,1) 80%)",
                                    width: `${CARD_WIDTH * opponentScale}px`,
                                    height: `${CARD_HEIGHT * opponentScale}px`,
                                    padding: "0.08rem",
                                    marginLeft: `${(GameConstant.startingCardsCount - (visibleCount - 1) / 2) * cardGap}px`,
                                    zIndex: cards.length * 2,
                                }}>
                                    <div className="w-full h-full flex items-center justify-center rounded-sm" style={{ background: "linear-gradient(145deg, rgba(73,72,84,1) 30%, rgba(15,14,15,1) 80%)" }}>
                                        <span className="text-white font-bold text-[0.6rem] text-[#fef8dc]">{cardsCount - GameConstant.startingCardsCount}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {isTyping && (
                        <Tooltip key="typing" text="..." visible placement="top" duration={0} className="absolute inset-0 z-[2]">
                            <div className="w-full h-full" />
                        </Tooltip>
                    )}
                    <Tooltip key={usernameKey} text={roomPlayer.player.username} visible={showUsername} placement="top" duration={2000} className="absolute inset-0 z-[1] cursor-pointer" onClick={() => { setShowUsername(true); setUsernameKey((k) => k + 1); }}>
                        <Avatar src={roomPlayer.player.avatar} className={`w-full h-full ${roomIsPlaying && !isActive ? "brightness-50" : ""}`} />
                    </Tooltip>

                    {isActive && <CircleProgress turnEndsAt={roomPlayer.turnEndsAt} turnDuration={roomPlayer.turnDuration} />}

                    <RoomPlayerEmote playerId={roomPlayer.player.id} />
                    <RoomPlayerAction roomPlayer={roomPlayer} />
                    <SwapTarget roomPlayer={roomPlayer} />
                </>
            )}

            {!roomPlayer && (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-white/70 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
            )}
        </div>
    )
});

export default Opponent;
